import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

const RESOURCE = "/settings/messaging";

export type MessagingChannelMode = "pooled" | "tenant_number" | "disabled";
export type NotificationChannelPreference = "both" | "whatsapp_only" | "email_only";

export interface MessagingSettings {
  messaging_channel_mode: MessagingChannelMode;
  notification_channel_preference: NotificationChannelPreference;
  client_contact_whatsapp: string | null;
  appointment_created_notifications_enabled: boolean;
  appointment_reminder_notifications_enabled: boolean;
  appointment_reminder_hours_before: number;
  appointment_reminder_hours_before_list: number[];
  notify_on_created_default: boolean;
  notify_reminder_default: boolean;
}

export interface MessagingUsage {
  sent_count: number;
  pack_bonus: number;
  limit: number;
  effective_limit: number;
  remaining_count: number;
  is_free: boolean;
  is_near_limit: boolean;
  is_at_limit: boolean;
  mode: MessagingChannelMode;
  period_month: string;
}

export interface MessagingHealth {
  pooled_circuit_open: boolean;
  pooled_circuit_open_at: string | null;
  iugis_messaging_active: boolean;
  iugis_ready?: boolean;
  iugis_readiness?: {
    ready: boolean;
    has_token: boolean;
    has_channel_key: boolean;
    has_template_created: boolean;
    has_template_reminder: boolean;
    has_template_club_join?: boolean;
    template_club_join?: string | null;
    missing: string[];
  };
}

export interface MessagingPackCatalogItem {
  sku: string;
  bonus: number;
  price_cents: number;
  label_key: string;
}

export interface MessagingSettingsData {
  settings: MessagingSettings;
  usage: MessagingUsage;
  health: MessagingHealth;
  pack_catalog: MessagingPackCatalogItem[];
}

export type UpdateMessagingSettingsPayload = Partial<MessagingSettings>;

export const MESSAGING_REMINDER_HOUR_PRESETS = [1, 2, 12, 24] as const;

export const IUGIS_REMINDER_HOUR_OPTIONS = [1, 2, 6, 12, 24, 48] as const;

export const MAX_IUGIS_REMINDER_HOURS = 5;

export const getMessagingSettings = (): Promise<ApiResponse<MessagingSettingsData>> =>
  api.get<ApiResponse<MessagingSettingsData>>(RESOURCE);

export const updateMessagingSettings = (
  payload: UpdateMessagingSettingsPayload
): Promise<ApiResponse<MessagingSettingsData>> =>
  api.update<ApiResponse<MessagingSettingsData>>(RESOURCE, payload);

export const purchaseMessagingPack = (
  sku: string
): Promise<ApiResponse<MessagingSettingsData>> =>
  api.create<ApiResponse<MessagingSettingsData>>(`${RESOURCE}/purchase-pack`, { sku });

export const isWhatsAppUsageBlocked = (usage: MessagingUsage): boolean =>
  usage.is_free || usage.is_at_limit;

export const isWhatsAppUsageNearLimit = (usage: MessagingUsage): boolean =>
  !usage.is_free && usage.is_near_limit && !usage.is_at_limit;

export const isWhatsAppChannelAvailable = (
  settings: Pick<MessagingSettings, "messaging_channel_mode">,
  usage: MessagingUsage
): boolean =>
  settings.messaging_channel_mode === "pooled" && !usage.is_free && !usage.is_at_limit;

export const isTenantNumberMessagingActive = (
  settings: Pick<MessagingSettings, "messaging_channel_mode">,
  health: Pick<MessagingHealth, "iugis_messaging_active">
): boolean =>
  settings.messaging_channel_mode === "tenant_number" && health.iugis_messaging_active;

export const prefersWhatsAppChannel = (
  settings: Pick<MessagingSettings, "notification_channel_preference" | "messaging_channel_mode">
): boolean =>
  settings.messaging_channel_mode !== "disabled"
  && settings.notification_channel_preference !== "email_only";

export const isWhatsAppDeliveryPossible = (
  settings: MessagingSettings,
  usage: MessagingUsage,
  health: MessagingHealth
): boolean =>
  prefersWhatsAppChannel(settings)
  && (
    isWhatsAppChannelAvailable(settings, usage)
    || isTenantNumberMessagingActive(settings, health)
  );

export type MessagingOutboundPreviewType = "created" | "reminder" | "review_request";

export interface MessagingOutboundPreview {
  type: MessagingOutboundPreviewType;
  body: string;
  hours_before: number | null;
}

export const getMessagingOutboundPreview = (
  type: MessagingOutboundPreviewType,
  hoursBefore = 24
): Promise<ApiResponse<MessagingOutboundPreview>> =>
  api.get<ApiResponse<MessagingOutboundPreview>>(
    `${RESOURCE}/preview-outbound?type=${encodeURIComponent(type)}&hours_before=${hoursBefore}`
  );
